import React from 'react';

export type SpinnerSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type SpinnerVariant = 'border' | 'dots' | 'pulse';
export type TextPosition = 'above' | 'below';

interface SpinnerProps {
  size?: SpinnerSize;
  variant?: SpinnerVariant;
  color?: string;
  text?: string;
  textPosition?: TextPosition;
  className?: string;
  centered?: boolean;
  fullHeight?: boolean;
  minHeight?: string;
}

const Spinner: React.FC<SpinnerProps> = ({
    size = 'md',
    variant = 'border',
    color = 'info',
    text,
    textPosition = 'below',
    className = '',
    centered = false,
    fullHeight = false,
    minHeight
}) => {
    const sizeClasses = {
        xs: 'spinner-xs',
        sm: 'spinner-sm', 
        md: '',
        lg: 'spinner-lg',
        xl: 'spinner-xl'
    };

    const sizeStyles = {
        xs: {width: '16px', height: '16px'},
        sm: {width: '20px', height: '20px'},
        md: {width: '32px', height: '32px'},
        lg: {width: '48px', height: '48px'},
        xl: {width: '64px', height: '64px'}
    };

    const renderSpinner = () => {
        const spinnerStyle = sizeStyles[size];

        switch (variant) {
            case 'dots':
                return (
                    <div className="spinner-dots">
                        <div className="spinner-dot" />
                        <div className="spinner-dot" />
                        <div className="spinner-dot" />
                    </div>
                );
      
            case 'pulse':
                return (
                    <div 
                        className="spinner-pulse"
                        style={spinnerStyle}
                    />
                );
      
            case 'border':
            default:
                return (
                    <div 
                        className={`spinner-border text-${color} ${sizeClasses[size]}`}
                        style={spinnerStyle}
                        role="status"
                        aria-label="Loading"
                    />
                );
        }
    };

    const containerClasses = [
        'spinner-container',
        centered ? 'spinner-centered' : '',
        fullHeight ? 'spinner-full-height' : '',
        className
    ].filter(Boolean).join(' ');

    const containerStyle: React.CSSProperties = {
        ...(centered && {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column'
        }),
        ...(fullHeight && {minHeight: '100vh'}),
        ...(minHeight && {minHeight})
    };

    return (
        <div className={containerClasses} style={containerStyle}>
            {text && textPosition === 'above' && (
                <div className={`spinner-text ${size === 'xs' || size === 'sm' ? 'text-sm' : ''}`}>
                    {text}
                </div>
            )}
            {renderSpinner()}
            {text && textPosition === 'below' && (
                <div className={`spinner-text ${size === 'xs' || size === 'sm' ? 'text-sm' : ''}`}>
                    {text}
                </div>
            )}
        </div>
    );
};

export default Spinner;
